import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { Badge } from '@mui/icons-material';
import { AdfsItemFragment, IdentityProviderSummaryItemFragment } from '../__generated__/graphql';
import { AdfsSetting } from '../interfaces';
import { GET_ADFS, GET_SINGLE_ADFS_BY_BKS_ID, UPDATE_ADVANCE_SETTING, UPDATE_GENERAL_SETTING } from '../administration/ADFSConfig/ADFSConfig.graphql';
import TenantID from '../administration/ADFSConfig/TenantID';
import ClientID from '../administration/ADFSConfig/ClientID';
import ClientSecret from '../administration/ADFSConfig/ClientSecret';
import EmailDomain from '../administration/ADFSConfig/EmailDomain';
import { GET_IDENTITY_PROVIDER_SUMMARY, TEST_CLIENT_SECRET } from './Sso.graphql';
import { describeSecret, secretState } from './clientSecret';
import { SsoField, SsoSection } from './SsoSection';
import { parseExpiryDate, toExpiryInput } from './expiryDate';
import CallbackPathField from './CallbackPathField';
import CopyButton from './CopyButton';
import { redirectUri, stripSlashes } from './callbackPath';

interface Props {
  adfs: AdfsItemFragment;
  summary: IdentityProviderSummaryItemFragment;
}

/** Identity's implicit flow never sends the secret, so this test is the only check that it works. */
function SecretField({ summary }: { summary: IdentityProviderSummaryItemFragment }) {
  const [error, setError] = useState<string | null>(null);
  const [test, { loading }] = useMutation(TEST_CLIENT_SECRET, {
    refetchQueries: [{ query: GET_IDENTITY_PROVIDER_SUMMARY, variables: { bksID: summary.bksID } }],
    awaitRefetchQueries: true,
  });
  const state = secretState(summary);
  const color = state === 'valid' ? 'success.main' : state === 'invalid' ? 'error.main' : state === 'unknown' ? 'warning.main' : undefined;

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" component="div">
        Klienthemmelighet
      </Typography>
      <Typography variant="body2" color={color}>
        {loading ? 'Tester mot Entra …' : describeSecret(summary)}
      </Typography>
      {error && (
        <Typography variant="body2" color="error.main">
          {error}
        </Typography>
      )}
      <Button
        size="small"
        sx={{ mt: 0.5, px: 0 }}
        disabled={loading}
        onClick={async () => {
          setError(null);
          try {
            await test({ variables: { input: { bksID: summary.bksID } } });
          } catch (e: any) {
            setError(e?.message ?? 'Kunne ikke teste klienthemmeligheten.');
          }
        }}
      >
        Test klienthemmelighet
      </Button>
    </Box>
  );
}

function RedirectUriField({ uri, path }: { uri: string | null; path: string | null | undefined }) {
  return (
    <Box sx={{ minWidth: 0, gridColumn: { md: 'span 2' } }}>
      <Typography variant="caption" color="text.secondary" component="div">
        Redirect-URI (til kundens app-registrering)
      </Typography>
      {uri ? (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {uri}
          </Typography>
          <CopyButton value={uri} label="Kopier redirect-URI" />
        </Stack>
      ) : (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {path || '—'}
        </Typography>
      )}
    </Box>
  );
}

export default function SsoLoginSection({ adfs, summary }: Props) {
  const [editing, setEditing] = useState(false);

  return (
    <SsoSection
      title="Identitetsleverandør (Entra ID)"
      icon={<Badge fontSize="small" />}
      action={
        !editing && (
          <Button size="small" variant="outlined" onClick={() => setEditing(true)}>
            Rediger
          </Button>
        )
      }
    >
      {editing ? (
        <LoginForm adfs={adfs} summary={summary} onDone={() => setEditing(false)} />
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(4, minmax(0, 1fr))' }, gap: 2 }}>
          <SsoField label="Leverandør-ID (brukes av Identity og portalen)" value={adfs.scheme} mono />
          <SsoField label="E-postdomener" value={(adfs.emailDomains ?? []).join(', ')} />
          <SsoField label="Tenant-ID" value={adfs.tenantID} mono />
          <SsoField label="Klient-ID" value={adfs.clientID} mono />
          <SecretField summary={summary} />
          <RedirectUriField uri={redirectUri(summary.identityBaseUrl, stripSlashes(adfs.callbackPath))} path={adfs.callbackPath} />
        </Box>
      )}
    </SsoSection>
  );
}

function LoginForm({ adfs, summary, onDone }: Props & { onDone: () => void }) {
  const originalScheme = adfs.scheme ?? '';
  const originalCallback = stripSlashes(adfs.callbackPath);

  // Fixed at setup; see UpdateGeneralSetting.
  const scheme = originalScheme;
  const [tenantID, setTenantID] = useState(adfs.tenantID ?? '');
  const [clientID, setClientID] = useState(adfs.clientID ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [emailDomains, setEmailDomains] = useState((adfs.emailDomains ?? []).join(','));
  const [callbackPath, setCallbackPath] = useState(originalCallback);
  const [errors, setErrors] = useState({ domains: false, callback: false });
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data: all } = useQuery(GET_ADFS);
  const providers = all?.adfs as AdfsSetting[] | undefined;
  const otherPaths = (providers ?? []).filter((x) => x.id !== adfs.id).map((x) => x.callbackPath ?? '');

  const refetchQueries = [
    { query: GET_SINGLE_ADFS_BY_BKS_ID, variables: { bksID: summary.bksID } },
    { query: GET_IDENTITY_PROVIDER_SUMMARY, variables: { bksID: summary.bksID } },
  ];
  const [updateGeneral, { loading: savingGeneral }] = useMutation(UPDATE_GENERAL_SETTING);
  const [updateAdvance, { loading: savingAdvance }] = useMutation(UPDATE_ADVANCE_SETTING);
  const [testSecret, { loading: testingSecret }] = useMutation(TEST_CLIENT_SECRET);
  const saving = savingGeneral || savingAdvance || testingSecret;

  const valid = scheme && tenantID && clientID && emailDomains && callbackPath && !errors.domains && !errors.callback;

  const save = async () => {
    setSaveError(null);
    try {
      await updateGeneral({
        variables: {
          input: {
            id: adfs.id,
            bksID: summary.bksID,
            scheme,
            tenantID,
            clientID,
            clientSecret,
            emailDomains,
            // Not edited in this form; sent back unchanged because the mutation requires them.
            displayName: adfs.displayName ?? '',
            expiryDate: toExpiryInput(parseExpiryDate(adfs.expiryDate)),
            isRestricted: summary.isRestricted,
            extraUserPercentage: summary.extraUserPercentage,
          },
        },
        refetchQueries,
        awaitRefetchQueries: true,
      });
      if (callbackPath !== originalCallback) {
        await updateAdvance({
          variables: { input: { id: adfs.id, callbackPath, scheme } },
          refetchQueries,
          awaitRefetchQueries: true,
        });
      }
      if (clientSecret) {
        await testSecret({ variables: { input: { bksID: summary.bksID } }, refetchQueries, awaitRefetchQueries: true });
      }
      onDone();
    } catch (e: any) {
      setSaveError(e?.message ?? 'Kunne ikke lagre.');
    }
  };

  return (
    // Stack spacing replaces the fields' own margin, so outlined labels get room above them.
    <Stack spacing={2.5}>
      <TextField
        label="Leverandør-ID (scheme)"
        fullWidth
        value={scheme}
        disabled
        helperText="Settes ved oppsett og kan ikke endres: Identity og portalen kjenner leverandøren under den."
      />
      <TenantID existingTenant={tenantID} callbackValue={setTenantID} />
      <ClientID existingId={clientID} callbackValue={setClientID} />
      <ClientSecret hasExistingSecret callbackValue={setClientSecret} />
      <EmailDomain
        result={providers}
        id={adfs.id}
        domain={(adfs.emailDomains ?? []).filter((x): x is string => !!x)}
        callbackValue={setEmailDomains}
        callbackError={(error) => setErrors((x) => ({ ...x, domains: error }))}
      />
      <CallbackPathField
        scheme={scheme}
        initialPath={adfs.callbackPath ?? ''}
        otherPaths={otherPaths}
        identityBaseUrl={summary.identityBaseUrl}
        onChange={(path, pathValid) => {
          setCallbackPath(path);
          setErrors((x) => ({ ...x, callback: !pathValid }));
        }}
      />
      {callbackPath !== originalCallback && (
        <Alert severity="warning">
          Kundens app-registrering i Entra må ha den nye redirect-URI-en før endringen lagres, ellers slutter innloggingen å virke.
        </Alert>
      )}
      {saveError && <Alert severity="error">{saveError}</Alert>}
      <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
        <Button variant="contained" disabled={!valid || saving} onClick={save}>
          Lagre
        </Button>
        <Button disabled={saving} onClick={onDone}>
          Avbryt
        </Button>
      </Stack>
    </Stack>
  );
}
